import { isSelectionDescendantOf } from '@/lib/zy-tiptap-utils';
import type { Editor } from '@tiptap/core';
import { Text } from '@tiptap/extension-text';
import type { Node as PMNode } from '@tiptap/pm/model';

const NON_PARAGRAPH_FOCUSABLE_SELECTOR = [
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"]):not([disabled])',
    'button:not([disabled])',
].join(', ');

function inAListItem(editor: Editor): boolean {
    return isSelectionDescendantOf(editor, 'listItem');
}

function findEditorRootAndReferenceNode(editor: Editor): { editorRoot: HTMLElement; referenceNode: Node } {
    const { from } = editor.state.selection;
    const dom = editor.view.domAtPos(from);
    const editorRoot = editor.view.dom;
    return { editorRoot, referenceNode: dom.node };
}

export function findNextFocusableElement(container: HTMLElement, referenceNode: Node): HTMLElement | null {
    const focusables = Array.from(container.querySelectorAll<HTMLElement>(NON_PARAGRAPH_FOCUSABLE_SELECTOR));
    return focusables.find(element => referenceNode.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING) || null;
}

export function findPreviousFocusableElement(container: HTMLElement, referenceNode: Node): HTMLElement | null {
    const reverseFocusables = Array.from(container.querySelectorAll<HTMLElement>(NON_PARAGRAPH_FOCUSABLE_SELECTOR)).reverse();
    return reverseFocusables.find(element => referenceNode.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING) || null;
}

// Finds the first paragraph in the ProseMirror doc whose DOM element appears
// after referenceElement in document order. Used when Tab is pressed on a native DOM focusable.
export function findNextParagraphAfterElement(editor: Editor, referenceElement: HTMLElement): { pos: number; node: PMNode } | null {
    const doc = editor.state.doc;
    let result: { pos: number; node: PMNode } | null = null;
    doc.nodesBetween(0, doc.content.size, (node, pos) => {
        if (result) return false;
        if (['paragraph', 'heading'].includes(node.type.name)) {
            const paraDom = editor.view.nodeDOM(pos) as HTMLElement | null;
            if (paraDom && referenceElement.compareDocumentPosition(paraDom) & Node.DOCUMENT_POSITION_FOLLOWING) {
                result = { pos, node };
                return false;
            }
        }
    });
    return result;
}

// Finds the last paragraph in the ProseMirror doc whose DOM element appears
// before referenceElement in document order. Used when Shift-Tab is pressed on a native DOM focusable.
export function findPreviousParagraphBeforeElement(editor: Editor, referenceElement: HTMLElement): { pos: number; node: PMNode } | null {
    const doc = editor.state.doc;
    let result: { pos: number; node: PMNode } | null = null;
    doc.nodesBetween(0, doc.content.size, (node, pos) => {
        if (['paragraph', 'heading'].includes(node.type.name)) {
            const paraDom = editor.view.nodeDOM(pos) as HTMLElement | null;
            if (paraDom && referenceElement.compareDocumentPosition(paraDom) & Node.DOCUMENT_POSITION_PRECEDING) {
                result = { pos, node };
            }
        }
    });
    return result;
}

// Returns the ProseMirror positions immediately before and after the paragraph
// that contains the current cursor, or null if the cursor is not in a paragraph.
function findCurrentParagraphBounds(editor: Editor): { before: number; after: number } | null {
    const { from } = editor.state.selection;
    const resolved = editor.state.doc.resolve(from);
    for (let depth = resolved.depth; depth >= 0; depth--) {
        if (['paragraph', 'heading'].includes(resolved.node(depth).type.name)) {
            return { before: resolved.before(depth), after: resolved.after(depth) };
        }
    }
    return null;
}

function findNextParagraphAfter(editor: Editor, fromPos: number): { pos: number; node: PMNode } | null {
    const doc = editor.state.doc;
    let result: { pos: number; node: PMNode } | null = null;
    doc.nodesBetween(fromPos, doc.content.size, (node, pos) => {
        if (result) return false;
        if (['paragraph', 'heading'].includes(node.type.name)) {
            result = { pos, node };
            return false;
        }
    });
    return result;
}

function findPreviousParagraphBefore(editor: Editor, beforePos: number): { pos: number; node: PMNode } | null {
    const doc = editor.state.doc;
    let result: { pos: number; node: PMNode } | null = null;
    doc.nodesBetween(0, beforePos, (node, pos) => {
        if (['paragraph', 'heading'].includes(node.type.name)) {
            result = { pos, node };
        }
    });
    return result;
}

export const ZyText = Text.extend({
    // Tab navigates to the next ordered element after the cursor:
    //   - If the next element is a paragraph, the ProseMirror cursor moves to its end (stays in contenteditable).
    //   - If the next element is a real DOM focusable (input, button, etc.), .focus() is called on it.
    // Shift-Tab mirrors this logic going backward.
    // When inside a list item, Tab/Shift-Tab indent/outdent instead.
    addKeyboardShortcuts() {
        return {
            Tab: () => {
                if (!this.editor.isEditable) return false;

                if (inAListItem(this.editor)) {
                    const didSink = this.editor.commands.sinkListItem('listItem');
                    if (didSink) return true;
                }

                const { editorRoot, referenceNode } = findEditorRootAndReferenceNode(this.editor);
                const nextNonParaFocusable = findNextFocusableElement(editorRoot, referenceNode);

                const bounds = findCurrentParagraphBounds(this.editor);
                const nextPara = bounds ? findNextParagraphAfter(this.editor, bounds.after) : null;

                if (!nextPara && !nextNonParaFocusable) {
                    return true;
                }

                if (!nextPara) {
                    nextNonParaFocusable!.focus();
                    return true;
                }

                if (!nextNonParaFocusable) {
                    const endPos = nextPara.pos + nextPara.node.nodeSize - 1;
                    this.editor.commands.focus(endPos, { scrollIntoView: true });
                    return true;
                }

                // Both exist: compare document order to determine which comes first.
                const nextParaDom = this.editor.view.nodeDOM(nextPara.pos) as HTMLElement | null;
                const paraIsFirst =
                    nextParaDom && nextNonParaFocusable.compareDocumentPosition(nextParaDom) & Node.DOCUMENT_POSITION_PRECEDING;

                if (paraIsFirst) {
                    const endPos = nextPara.pos + nextPara.node.nodeSize - 1;
                    this.editor.commands.focus(endPos, { scrollIntoView: true });
                } else {
                    nextNonParaFocusable.focus();
                }

                return true;
            },
            'Shift-Tab': () => {
                if (!this.editor.isEditable) return false;

                if (inAListItem(this.editor)) {
                    const didLift = this.editor.commands.liftListItem('listItem');
                    if (didLift) return true;
                }

                const { editorRoot, referenceNode } = findEditorRootAndReferenceNode(this.editor);
                const prevNonParaFocusable = findPreviousFocusableElement(editorRoot, referenceNode);

                const bounds = findCurrentParagraphBounds(this.editor);
                const prevPara = bounds ? findPreviousParagraphBefore(this.editor, bounds.before) : null;

                if (!prevPara && !prevNonParaFocusable) {
                    return true;
                }

                if (!prevPara) {
                    prevNonParaFocusable!.focus();
                    return true;
                }

                if (!prevNonParaFocusable) {
                    const endPos = prevPara.pos + prevPara.node.nodeSize - 1;
                    this.editor.commands.focus(endPos, { scrollIntoView: true });
                    return true;
                }

                // Both exist: going backward, the paragraph is "closer" if it appears
                // after the non-para focusable in the document.
                const prevParaDom = this.editor.view.nodeDOM(prevPara.pos) as HTMLElement | null;
                const paraIsCloser =
                    prevParaDom && prevNonParaFocusable.compareDocumentPosition(prevParaDom) & Node.DOCUMENT_POSITION_FOLLOWING;

                if (paraIsCloser) {
                    const endPos = prevPara.pos + prevPara.node.nodeSize - 1;
                    this.editor.commands.focus(endPos, { scrollIntoView: true });
                } else {
                    prevNonParaFocusable.focus();
                }

                return true;
            },
        };
    },
});
